import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from '../../utils/axiosConfig';

const ChatWidget = ({ botId }) => {
  const [bot, setBot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBot();
  }, [botId]);

  const loadBot = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/bots/${botId}`);
      setBot(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Bots:', error);
      setError('Bot nicht gefunden');
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/chat/send`, {
        message: inputMessage,
        bot_id: botId,
        system_prompt: bot?.system_prompt
      });

      const botMessage = {
        id: Date.now() + 1,
        text: response.data.response,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Fehler beim Senden der Nachricht:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!bot) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#ffffff'
    }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        backgroundColor: bot.widget_color || '#FFD700',
        color: '#000000',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Avatar sx={{ bgcolor: 'rgba(0,0,0,0.1)' }}>
          <BotIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {bot.name}
          </Typography>
          <Typography variant="caption">
            {bot.description || 'KI-Assistent'}
          </Typography>
        </Box>
      </Box>

      {/* Messages */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        {messages.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            textAlign: 'center',
            p: 2
          }}>
            <Typography variant="body2" color="text.secondary">
              Hallo! Wie kann ich Ihnen helfen?
            </Typography>
          </Box>
        )}
        
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
              mb: 1
            }}
          >
            <Paper
              sx={{
                p: 1.5,
                maxWidth: '80%',
                backgroundColor: message.sender === 'user' 
                  ? (bot.widget_color || '#FFD700')
                  : '#f5f5f5',
                color: message.sender === 'user' ? '#000000' : '#000000'
              }}
            >
              <Typography variant="body2">
                {message.text}
              </Typography>
            </Paper>
          </Box>
        ))}
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Paper sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">Bot tippt...</Typography>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Input */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Nachricht eingeben..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
              }
            }}
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={!inputMessage.trim() || loading}
            sx={{
              minWidth: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: bot.widget_color || '#FFD700',
              '&:hover': {
                backgroundColor: bot.widget_color || '#F57F17',
              }
            }}
          >
            <SendIcon />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatWidget;
